import { checkIsErrorToolOutput } from "@repo/app-types/agents/errorToolOutput";
import {
  ChatToolBase,
  ChatToolDisclosure,
  type ToolComponent,
} from "@repo/ui/chat/chatToolCall";
import { IconMessages } from "@tabler/icons-react";
import type { FC } from "react";
import {
  SimulatedCallTranscript,
  truncate,
  type SimulatedCallTranscriptMessage,
} from "./simulatedCallTranscript.js";

/** The retired tool's call, as past threads store it. */
interface SimulateVoiceAgentConversationToolInput {
  scenario: string;
}

interface SimulateAgentConversationToolOutput {
  transcript: SimulatedCallTranscriptMessage[];
  commentary: string;
}

/** Renders the retired simulate-voice-agent-conversation calls in past threads. */
export const SimulateVoiceAgentConversationTool: ToolComponent<
  SimulateVoiceAgentConversationToolInput,
  SimulateAgentConversationToolOutput
> = (props) => {
  const scenario = props.input.scenario;
  const isRunning = props.status.type === "running";
  const isComplete = props.status.type === "complete";

  // If running, show what's being simulated
  if (isRunning) {
    const testingText = scenario
      ? `Simulating: "${truncate(scenario, 50)}"`
      : "Running simulation...";

    return <ChatToolBase status={props.status}>{testingText}</ChatToolBase>;
  }

  if (checkIsErrorToolOutput(props.output)) {
    return (
      <ChatToolBase status={props.status}>
        An error occurred while running the simulation
      </ChatToolBase>
    );
  }

  // If complete, show expandable results
  // Validate that result has the expected shape before rendering
  if (
    isComplete &&
    props.output &&
    Array.isArray(props.output.transcript) &&
    typeof props.output.commentary === "string"
  ) {
    return (
      <ChatToolDisclosure
        status={props.status}
        icon={
          <IconMessages className="mt-0.5 size-3.5 shrink-0 text-indigo-500" />
        }
        label={<>Simulation: {scenario ? `"${scenario}"` : "completed"}</>}
      >
        <SimulationDetails scenario={scenario} result={props.output} />
      </ChatToolDisclosure>
    );
  }

  // Fallback
  return (
    <ChatToolBase status={props.status}>Running simulation...</ChatToolBase>
  );
};

interface SimulationDetailsProps {
  scenario: string;
  result: SimulateAgentConversationToolOutput;
}

const SimulationDetails: FC<SimulationDetailsProps> = ({
  scenario,
  result,
}) => {
  const { transcript, commentary } = result;

  return (
    <div className="space-y-2">
      {/* Show the scenario */}
      <div className="space-y-1">
        <div className="text-[10px] font-medium text-gray-400">Scenario</div>
        <div className="text-xs text-gray-600">{scenario}</div>
      </div>

      <SimulatedCallTranscript messages={transcript} />

      {/* Show the commentary/analysis */}
      {commentary && (
        <div className="space-y-1">
          <div className="text-[10px] font-medium text-gray-400">Analysis</div>
          <div className="rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs whitespace-pre-wrap text-amber-900">
            {commentary}
          </div>
        </div>
      )}
    </div>
  );
};
